<template>
  <UIModal id="edit-email-notification-modal" :show="showModal" :width="750">
    <template #header>
      <UIModalHeader
        id="edit-email-notification-header"
        class="border-b-2"
        :title="headerTitle"
        @close="closeModal"
      />
    </template>
    <!-- content -->
    <UIModalContent>
      <!-- tabs -->
      <div class="mt-4">
        <UIAlert id="notification-alert" :closable="false">
          <template #content>{{ note }}</template>
        </UIAlert>
      </div>
      <div class="mt-2 flex border-b-2 bg-white px-2">
        <div
          v-for="tab in tabs"
          v-show="tab.visible"
          :key="tab.key"
          class="w-44 cursor-pointer py-2 text-center text-gray-400"
          :class="{
            'border-b-2 border-primary-700 text-primary-700':
              selectedTab === tab.key,
          }"
          role="button"
          tabindex="0"
          @click="selectedTab = tab.key"
          @keypress.enter="selectedTab = tab.key"
        >
          {{ getTabLabel(tab.key) }}
        </div>
      </div>

      <div v-if="state.isLoading">
        <UISpin
          class="flex h-[100px] items-center justify-center overflow-auto"
        />
      </div>
      <EmailTemplateConfiguratorTab
        v-else-if="selectedTab === 'email'"
        ref="emailConfigurator"
        @update-notification="val => emit('update-notification', val)"
      />
      <InAppConfiguratorTab
        v-else-if="selectedTab === 'inApp'"
        @update-notification="val => emit('update-notification', val)"
      />
      <SmsTemplateConfiguratorTab
        v-else-if="selectedTab === 'sms'"
        ref="smsConfigurator"
        @update-notification="val => emit('update-notification', val)"
      />
      <WhatsAppTemplateConfiguratorTab
        v-else-if="selectedTab === 'whatsapp'"
        ref="whatsappConfigurator"
        @update-notification="val => emit('update-notification', val)"
      />
    </UIModalContent>
    <!-- footer button -->
    <template #footer>
      <UIModalFooter
        id="modal-footer"
        class="border-t-2 pt-5"
        :disabled="state.saving || state.disableSave"
        :loading="state.saving"
        :positive-text="t('calendar_advanced.general.save')"
        :negative-text="t('calendar_advanced.general.close')"
        @positive-click="save"
        @negative-click="closeModal"
      />
    </template>
  </UIModal>
</template>

<script setup lang="ts">
import { EventNotificationService } from '@/class/services/EventNotificationService'
import { useNotify } from '@/components/composition/notification'
import AppState, { getEmailTemplates, getSmsTemplates } from '@/states/app'
import {
  getDefaultNotification,
  getDefaultNotifications,
  sortNotificationsByReceiverType,
  eventNotificationState as state,
  updateNotificationState,
  applyInAppCreateResult,
  stripNotificationIdForCreate,
  isVirtualNotification,
  buildSavePayloadFromNotifications,
} from '@/states/EventNotificationState'
import {
  AltType,
  EmailNotification,
  NotificationChannel,
  ReceiverType,
} from '@/types/notification'
import { cleanHTMLForEmail } from '@gohighlevel/ghl-text-editor'
import {
  UIAlert,
  UIModal,
  UIModalFooter,
  UIModalHeader,
  UISpin,
} from '@gohighlevel/ghl-ui'
import { PropType, computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import EmailTemplateConfiguratorTab from './EmailTemplateConfiguratorTab.vue'
import InAppConfiguratorTab from './InAppConfiguratorTab.vue'
import SmsTemplateConfiguratorTab from './SmsTemplateConfiguratorTab.vue'
import WhatsAppTemplateConfiguratorTab from './WhatsAppTemplateConfiguratorTab.vue'
const { sendNotification } = useNotify()
const { t } = useI18n()
const selectedTab = ref('email')
const emailConfigurator = ref(null) as any
const smsConfigurator = ref(null) as any
const whatsappConfigurator = ref(null) as any
const whatsappTemplatesLoaded = ref(false)
const emit = defineEmits(['update-notification'])
const emailTitles = {
  booked: {
    title: t('eventNotification.list.booked.modalTitle'),
    note: t('eventNotification.list.booked.note'),
  },
  confirmation: {
    title: t('eventNotification.list.confirmation.modalTitle'),
    note: t('eventNotification.list.confirmation.note'),
  },
  cancellation: {
    title: t('eventNotification.list.cancellation.modalTitle'),
    note: t('eventNotification.list.cancellation.note'),
  },
  reschedule: {
    title: t('eventNotification.list.reschedule.modalTitle'),
    note: t('eventNotification.list.reschedule.note'),
  },
  reminder: {
    title: t('eventNotification.list.reminder.modalTitle'),
    note: t('eventNotification.list.reminder.note'),
  },
  followup: {
    title: t('eventNotification.list.followup.modalTitle'),
    note: t('eventNotification.list.followup.note'),
  },
}

const props = defineProps({
  showModal: Boolean,
  closeModal: {
    type: Function,
    required: true,
  },
  notification: {
    type: Object as PropType<EmailNotification>,
    required: true,
  },
  calendarId: {
    type: String,
    required: true,
  },
})

const tabs = computed(() => [
  {
    key: 'email',
    label: 'eventNotification.email.channelName',
    visible: true,
  },
  {
    key: 'inApp',
    label: 'eventNotification.inApp.channelName',
    visible: true,
  },
  {
    key: 'sms',
    label: 'eventNotification.sms.channelName',
    visible: AppState.isSmsNotificationsEnabled,
  },
  {
    key: 'whatsapp',
    label: 'eventNotification.whatsapp.channelName',
    visible: AppState.isWhatsappNotificationsEnabled,
  },
])

function getTabLabel(tabKey: string) {
  switch (tabKey) {
    case 'email':
      return t('eventNotification.email.channelName')
    case 'inApp':
      return t('eventNotification.inApp.channelName')
    case 'sms':
      return t('eventNotification.sms.channelName')
    case 'whatsapp':
      return t('eventNotification.whatsapp.channelName')
    default:
      return ''
  }
}

onMounted(async () => {
  // load email and SMS templates
  state.isLoading = true
  state.calendarId = props.calendarId
  state.calendar = AppState.editingCalendar
  state.isEventCalendar = AppState.editingCalendar?.isEventCalendar || false
  state.isCustomFormSelected = !!AppState.editingCalendar?.formId
  await loadEmailTemplate()
  await loadSmsTemplates()
  // WhatsApp templates will be loaded when WhatsApp tab is first accessed
  await loadEventNotifications()
  state.isLoading = false
})

// Watch for WhatsApp tab selection to load templates lazily
watch(selectedTab, async newTab => {
  if (newTab === 'whatsapp') {
    if (!whatsappTemplatesLoaded.value) {
      // Template loading will be handled by WhatsAppTemplateConfiguratorTab
      // after it checks is-ready status
      whatsappTemplatesLoaded.value = true
    }
  }
})

const headerTitle = computed(() => {
  return `${t('labels.edit')} ${emailTitles[props.notification.type].title}`
})
const note = computed(() => {
  return `${emailTitles[props.notification.type].note}`
})
async function loadEmailTemplate() {
  await getEmailTemplates()
}

async function loadSmsTemplates() {
  await getSmsTemplates()
}

async function loadEventNotifications() {
  // get all email notification data
  state.accordions = []
  const defaultDataForAllReceivers = getDefaultNotifications({
    altId: props.calendarId,
    altType: AltType.CALENDAR,
    notificationType: props.notification.type, //confirmation
  })

  const { data } = await EventNotificationService.getEventNotifications({
    altId: props.calendarId,
    altType: AltType.CALENDAR,
    notificationType: props.notification.type,
  }).catch(() => {
    state.isLoading = false
  })

  const emailNotification =
    data?.filter(
      eventNotification =>
        eventNotification.channel === NotificationChannel.EMAIL
    ) || []
  const inAppData =
    data?.find(
      eventNotification =>
        eventNotification.channel === NotificationChannel.IN_APP
    ) || null
  const smsNotification =
    data?.filter(eventNotification => eventNotification.channel === 'sms') || []
  const whatsappNotification =
    data?.filter(
      eventNotification => eventNotification.channel === 'whatsapp'
    ) || []
  // set email data
  if (emailNotification?.length) {
    defaultDataForAllReceivers.forEach(notification => {
      const found = emailNotification.find(
        eventNotification =>
          eventNotification.receiverType === notification.receiverType
      )
      if (!found) {
        emailNotification.push(
          Object.assign({}, notification, { isActive: false })
        )
      }
    })
    // Sort email notifications by receiver type to ensure consistent ordering
    state.eventNotifications = sortNotificationsByReceiverType(
      emailNotification,
      NotificationChannel.EMAIL
    )
  } else {
    state.eventNotifications = defaultDataForAllReceivers
    // saveEmailNotifications(defaultDataForAllReceivers)
  }
  //set InApp data
  const defaultInAppData = getDefaultNotification({
    altId: props.calendarId,
    altType: AltType.CALENDAR,
    notificationType: props.notification.type, //confirmation
    channel: NotificationChannel.IN_APP,
    receiverType: ReceiverType.ASSIGNED_USER,
  })
  state.inAppNotification = (inAppData || defaultInAppData) as any

  //set SMS data
  const defaultSmsDataForAllReceivers = getDefaultNotifications({
    altId: props.calendarId,
    altType: AltType.CALENDAR,
    notificationType: props.notification.type,
    channel: NotificationChannel.SMS,
  })

  if (smsNotification?.length) {
    defaultSmsDataForAllReceivers.forEach(notification => {
      const found = smsNotification.find(
        eventNotification =>
          eventNotification.receiverType === notification.receiverType
      )
      if (!found) {
        smsNotification.push(
          Object.assign({}, notification, { isActive: false })
        )
      }
    })
    // Sort SMS notifications by receiver type to ensure consistent ordering
    state.smsNotifications = sortNotificationsByReceiverType(
      smsNotification,
      NotificationChannel.SMS
    )
  } else {
    state.smsNotifications = defaultSmsDataForAllReceivers
  }

  //set WhatsApp data
  const defaultWhatsAppDataForAllReceivers = getDefaultNotifications({
    altId: props.calendarId,
    altType: AltType.CALENDAR,
    notificationType: props.notification.type,
    channel: NotificationChannel.WHATSAPP,
  })

  if (whatsappNotification?.length) {
    defaultWhatsAppDataForAllReceivers.forEach(notification => {
      const found = whatsappNotification.find(
        eventNotification =>
          eventNotification.receiverType === notification.receiverType
      )
      if (!found) {
        whatsappNotification.push(
          Object.assign({}, notification, { isActive: false })
        )
      }
    })
    // Sort WhatsApp notifications by receiver type to ensure consistent ordering
    state.whatsappNotifications = sortNotificationsByReceiverType(
      whatsappNotification,
      'whatsapp'
    )
  } else {
    state.whatsappNotifications = defaultWhatsAppDataForAllReceivers
  }

  // remove Assigned User notification if it is an event calendar
  state.eventNotifications = state.eventNotifications.filter(
    notification =>
      !(
        state.isEventCalendar &&
        notification.receiverType === ReceiverType.ASSIGNED_USER
      )
  )
  // Note: Guest notifications are intentionally kept in state.
  // Re-sort after filtering to maintain order
  state.eventNotifications = sortNotificationsByReceiverType(
    state.eventNotifications,
    NotificationChannel.EMAIL
  )

  // Apply same filtering to SMS notifications
  state.smsNotifications = state.smsNotifications.filter(
    notification =>
      !(
        state.isEventCalendar &&
        notification.receiverType === ReceiverType.ASSIGNED_USER
      )
  )
  state.smsNotifications = state.smsNotifications.filter(
    ({ receiverType }) =>
      receiverType !== ReceiverType.GUEST ||
      AppState.editCalendarValidationData.enableGuests
  )
  // Re-sort after filtering to maintain order
  state.smsNotifications = sortNotificationsByReceiverType(
    state.smsNotifications,
    NotificationChannel.SMS
  )

  // Apply same filtering to WhatsApp notifications
  state.whatsappNotifications = state.whatsappNotifications.filter(
    notification =>
      !(
        state.isEventCalendar &&
        notification.receiverType === ReceiverType.ASSIGNED_USER
      )
  )
  // Note: WhatsApp only supports contact, assigned user, and phone numbers - no guest support
  // Re-sort after filtering to maintain order
  state.whatsappNotifications = sortNotificationsByReceiverType(
    state.whatsappNotifications,
    'whatsapp'
  )

  // set accordions initial state for all notification types
  const allNotifications = [
    ...state.eventNotifications,
    ...state.smsNotifications,
    ...state.whatsappNotifications,
  ]

  allNotifications.forEach(notification => {
    state.accordions.push({
      id: notification._id,
      isOpen: false,
      isChecked: false,
    })
  })
  await checkAllNotifications()
}

async function checkAllNotifications() {
  if (
    state.enableNotification &&
    !state.inAppNotification?._id &&
    state.eventNotifications.every(notification => !notification.deleted)
  ) {
    state.eventNotifications.forEach(notification => {
      notification.deleted = !state.enableNotification
    })
    state.inAppNotification.deleted = !state.enableNotification
    await saveEmailNotifications(state.eventNotifications)
    await saveInAppNotifications(state.inAppNotification)
  }
}

async function save() {
  state.saving = true
  try {
    if (selectedTab.value === NotificationChannel.EMAIL) {
      if (emailConfigurator.value) {
        const { results, errorMessage } =
          await emailConfigurator.value.validateAll()
        if (results.includes(false)) {
          if (errorMessage) {
            sendNotification({
              type: 'error',
              title: errorMessage,
              icon: 'AlertCircleIcon',
            })
          }
          return
        }
      }
      const payload = buildSavePayloadFromNotifications(state.eventNotifications)

      await saveEmailNotifications(payload)
    } else if (selectedTab.value === NotificationChannel.IN_APP) {
      const payload = Object.assign(state.inAppNotification)
      await saveInAppNotifications(payload)
    } else if (selectedTab.value === NotificationChannel.SMS) {
      if (smsConfigurator.value) {
        const { results, errorMessage } =
          await smsConfigurator.value.validateAll()
        if (results.includes(false)) {
          if (errorMessage) {
            sendNotification({
              type: 'error',
              title: errorMessage,
              icon: 'AlertCircleIcon',
            })
          }
          return
        }
      }
      const payload = buildSavePayloadFromNotifications(state.smsNotifications)

      await saveSmsNotifications(payload)
    } else if (selectedTab.value === 'whatsapp') {
      if (whatsappConfigurator.value) {
        try {
          await whatsappConfigurator.value.validateAll()
        } catch (error) {
          console.error('WhatsApp Configurator Validation Error', error)
          state.saving = false
          return
        }
      }
      const payload = buildSavePayloadFromNotifications(state.whatsappNotifications)

      await saveWhatsAppNotifications(payload)
    }
  } catch (error) {
    console.error('Save Error', error)
  } finally {
    state.saving = false
  }

  sendNotification({
    type: 'success',
    title: t('eventNotification.saveToastMsg'),
    icon: 'CheckCircleIcon',
  })
}

async function saveInAppNotifications(payload) {
  try {
    if (!isVirtualNotification(payload._id)) {
      await EventNotificationService.updateEventNotifications(payload)
    } else {
      const { data } = await EventNotificationService.createEventNotifications(
        stripNotificationIdForCreate(payload)
      )
      applyInAppCreateResult(data, payload.deleted)
    }
  } catch (error) {
    console.error('InApp Notification Save Error', error)
  }
}

async function saveEmailNotifications(payload: any[]) {
  try {
    payload.forEach(notification => {
      notification.body &&
        (notification.body = cleanHTMLForEmail(notification.body))
    })
    const payloadToUpdate = payload.filter(
      notification => !isVirtualNotification(notification?._id)
    )
    const payloadToCreate = payload
      .filter(notification => isVirtualNotification(notification?._id))
      .map(stripNotificationIdForCreate)
    const deleted = payload[0]?.deleted
    if (payloadToUpdate.length) {
      const { data } = await EventNotificationService.updateEventNotifications(
        payloadToUpdate
      )
      updateNotificationState(data, deleted)
    }
    if (payloadToCreate.length) {
      const { data } = await EventNotificationService.createEventNotifications(
        payloadToCreate
      )
      updateNotificationState(data, deleted)
    }
  } catch (error) {
    console.error('Payload update or create Error', error)
    throw error
  }
  return true
}

async function saveSmsNotifications(payload: any[]) {
  try {
    // Transform SMS payload to ensure proper field mapping
    payload.forEach(notification => {
      // For SMS notifications, ensure channel is set to 'sms'
      notification.channel = NotificationChannel.SMS

      // Map phone numbers to the correct field based on receiver type
      if (
        notification.receiverType === ReceiverType.PHONE_NUMBERS &&
        notification.additionalPhoneNumbers
      ) {
        // For phone numbers receiver type, use additionalPhoneNumbers field
        notification.additionalPhoneNumbers =
          notification.additionalPhoneNumbers
        // Remove email field if it exists
        delete notification.additionalEmailIds
      }
    })

    const payloadToUpdate = payload.filter(
      notification => !isVirtualNotification(notification?._id)
    )
    const payloadToCreate = payload
      .filter(notification => isVirtualNotification(notification?._id))
      .map(stripNotificationIdForCreate)
    // Get deleted status from payload (all items have same deleted status)
    const deleted = payload[0]?.deleted
    if (payloadToUpdate.length) {
      const { data } = await EventNotificationService.updateEventNotifications(
        payloadToUpdate
      )
      updateNotificationState(data, deleted)
    }
    if (payloadToCreate.length) {
      const { data } = await EventNotificationService.createEventNotifications(
        payloadToCreate
      )
      updateNotificationState(data, deleted)
    }
  } catch (error) {
    console.error('SMS Payload update or create Error', error)
    throw error
  }
  return true
}

// Helper function to get default template ID for a notification type and receiver type
async function getDefaultTemplateIdForNotification(
  notificationType: string,
  receiverType: string
): Promise<string | null> {
  try {
    // Import the function to get hardcoded template name
    const { getDefaultWhatsAppTemplateLabel } = await import(
      '../../../utils/default-notification-template'
    )

    // Get the hardcoded template name
    const defaultTemplateName = getDefaultWhatsAppTemplateLabel(
      notificationType,
      receiverType
    )
    if (!defaultTemplateName) {
      return null
    }

    // Get default templates from template-status endpoint
    const { getWhatsAppDefaultTemplates } = await import('../../../states/app')
    const defaultTemplates = await getWhatsAppDefaultTemplates()

    if (!defaultTemplates || defaultTemplates.length === 0) {
      return null
    }

    // Find template ID by matching the name
    const matchingTemplate = defaultTemplates.find(template => {
      const templateName = template.templateName || template.name
      return templateName === defaultTemplateName
    })

    if (matchingTemplate) {
      const templateId = matchingTemplate.templateId || matchingTemplate.id
      return templateId
    } else {
      return null
    }
  } catch (error) {
    console.error('[WhatsApp Save] Error getting default template ID:', error)
    return null
  }
}

async function saveWhatsAppNotifications(payload: any[]) {
  try {
    // Transform WhatsApp payload to ensure proper field mapping
    for (const notification of payload) {
      // For WhatsApp notifications, ensure channel is set to 'whatsapp'
      notification.channel = 'whatsapp'

      // Map phone numbers to the correct field based on receiver type
      if (
        notification.receiverType === 'phoneNumbers' &&
        notification.additionalPhoneNumbers
      ) {
        // For phone numbers receiver type, use additionalPhoneNumbers field
        notification.additionalPhoneNumbers =
          notification.additionalPhoneNumbers
        // Remove email field if it exists
        delete notification.additionalEmailIds
      }

      // Auto-populate default template ID if notification is enabled but has no templateId
      if (!notification.deleted && !notification.templateId) {
        const defaultTemplateId = await getDefaultTemplateIdForNotification(
          notification.notificationType,
          notification.receiverType
        )
        if (defaultTemplateId) {
          notification.templateId = defaultTemplateId
        }
      }
    }

    const payloadToUpdate = payload.filter(
      notification => !isVirtualNotification(notification?._id)
    )
    const payloadToCreate = payload
      .filter(notification => isVirtualNotification(notification?._id))
      .map(stripNotificationIdForCreate)
    // Get deleted status from payload (all items have same deleted status)
    const deleted = payload[0]?.deleted
    if (payloadToUpdate.length) {
      const { data } = await EventNotificationService.updateEventNotifications(
        payloadToUpdate
      )
      updateNotificationState(data, deleted)
    }
    if (payloadToCreate.length) {
      const { data } = await EventNotificationService.createEventNotifications(
        payloadToCreate
      )
      updateNotificationState(data, deleted)
    }
  } catch (error) {
    console.error('WhatsApp Payload update or create Error', error)
    throw error
  }
  return true
}
</script>
